// Placeholder illustrations for the landing page, drawn in the app palette.
// Swap for real product screenshots once they exist.
//
// Motion hooks (see landing-motion.ts): elements tagged `data-anim` are animated when the
// illustration scrolls into view, grouped and ordered by `data-step`.
//   pop   - scales in from its centre
//   draw  - stroke is drawn along its path (path/ellipse/line only)
//   fade  - fades and slides up
//   drop  - falls in with a slight rotation (sticky notes)
// `data-idle` adds a gentle loop once the intro has played: float | pulse | blink.

import { BRAND } from '@/theme';

const INK = BRAND.ink;
const MUTED = '#a1a1aa';
const PINK = BRAND.pinkStrong;
const PINK_SOFT = '#fde3ed';
const GRAY = '#4b4b52';
const GRAY_SOFT = '#efeff2';
const YELLOW_SOFT = '#fff4d6';
const HAND_FONT = '"Comic Sans MS", "Chalkboard SE", "Segoe Print", system-ui, sans-serif';

type IllustrationProps = {
  label: string;
};

export function AiDiagramIllustration({ label }: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 480 320"
      role="img"
      aria-label={label}
      width="100%"
      height="100%"
      data-illustration="ai"
    >
      <g data-anim="fade" data-step="0">
        <rect
          x="24"
          y="22"
          width="260"
          height="46"
          rx="12"
          fill="#fff"
          stroke={GRAY}
          strokeWidth="1.5"
        />
        <circle cx="46" cy="45" r="9" fill={GRAY_SOFT} stroke={GRAY} strokeWidth="1.5" />
        <path
          d="M42 45l3 3 6-6"
          stroke={GRAY}
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
        />
        <rect x="64" y="38" width="170" height="6" rx="3" fill={MUTED} opacity="0.5" />
        <rect x="64" y="50" width="120" height="6" rx="3" fill={MUTED} opacity="0.35" />
        <rect x="238" y="36" width="2" height="18" rx="1" fill={PINK} data-idle="blink" />
      </g>
      <g data-anim="fade" data-step="1">
        <path
          d="M300 45c40 0 60 10 70 40"
          stroke={GRAY}
          strokeWidth="1.8"
          fill="none"
          strokeDasharray="5 5"
        />
        <path
          d="M364 78l6 9 5-10"
          stroke={GRAY}
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
        />
      </g>

      <g fontFamily={HAND_FONT} fontSize="14" fill={INK} textAnchor="middle">
        <g data-anim="pop" data-step="2">
          <g data-idle="pulse">
            <rect
              x="300"
              y="100"
              width="140"
              height="48"
              rx="10"
              fill={PINK_SOFT}
              stroke={PINK}
              strokeWidth="2"
            />
            <text x="370" y="129">
              Idea
            </text>
          </g>
        </g>
        <g data-anim="pop" data-step="4">
          <rect
            x="180"
            y="190"
            width="120"
            height="46"
            rx="10"
            fill="#fff"
            stroke={INK}
            strokeWidth="2"
          />
          <text x="240" y="218">
            Plan
          </text>
        </g>
        <g data-anim="pop" data-step="4">
          <rect
            x="330"
            y="190"
            width="120"
            height="46"
            rx="10"
            fill="#fff"
            stroke={INK}
            strokeWidth="2"
          />
          <text x="390" y="218">
            Build
          </text>
        </g>
        <g data-anim="pop" data-step="6">
          <path
            d="M250 268 l30 -22 l30 22 l-30 22z"
            fill={GRAY_SOFT}
            stroke={GRAY}
            strokeWidth="2"
          />
          <text x="280" y="273" fontSize="12">
            Ship?
          </text>
        </g>
      </g>
      <g stroke={INK} strokeWidth="2" fill="none" strokeLinecap="round">
        <path d="M350 148 C330 165 270 170 245 188" data-anim="draw" data-step="3" />
        <path d="M239 180l6 9 -10 2" data-anim="draw" data-step="4" />
        <path d="M385 148 C388 165 390 175 390 188" data-anim="draw" data-step="3" />
        <path d="M383 181l7 9 6-9" data-anim="draw" data-step="4" />
        <path d="M240 236 C245 245 255 250 262 256" data-anim="draw" data-step="5" />
        <path d="M390 236 C380 250 330 262 312 266" data-anim="draw" data-step="5" />
      </g>
    </svg>
  );
}

export function SketchCanvasIllustration({ label }: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 480 320"
      role="img"
      aria-label={label}
      width="100%"
      height="100%"
      data-illustration="sketch"
    >
      <defs>
        <pattern id="landing-dots" width="18" height="18" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.2" fill={MUTED} opacity="0.45" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="480" height="320" fill="url(#landing-dots)" />

      <g data-anim="drop" data-step="0">
        <g data-idle="float">
          <g transform="rotate(-4 110 110)">
            <rect
              x="46"
              y="48"
              width="130"
              height="118"
              rx="6"
              fill={YELLOW_SOFT}
              stroke="#d9b54a"
              strokeWidth="1.5"
            />
            <g fontFamily={HAND_FONT} fontSize="14" fill={INK}>
              <text x="60" y="80">
                Goals
              </text>
              <text x="60" y="108" fontSize="12">
                • launch beta
              </text>
              <text x="60" y="130" fontSize="12">
                • 100 users
              </text>
            </g>
          </g>
        </g>
      </g>

      <g data-anim="drop" data-step="1">
        <g data-idle="float">
          <g transform="rotate(3 330 90)">
            <rect
              x="270"
              y="40"
              width="130"
              height="96"
              rx="6"
              fill={GRAY_SOFT}
              stroke={GRAY}
              strokeWidth="1.5"
            />
            <text x="286" y="74" fontFamily={HAND_FONT} fontSize="14" fill={INK}>
              Risks
            </text>
            <rect x="286" y="90" width="90" height="6" rx="3" fill={GRAY} opacity="0.35" />
            <rect x="286" y="104" width="70" height="6" rx="3" fill={GRAY} opacity="0.25" />
          </g>
        </g>
      </g>

      <ellipse
        cx="150"
        cy="248"
        rx="70"
        ry="38"
        fill="#fff"
        stroke={PINK}
        strokeWidth="2.2"
        data-anim="draw"
        data-step="2"
      />
      <text
        x="150"
        y="253"
        fontFamily={HAND_FONT}
        fontSize="14"
        fill={INK}
        textAnchor="middle"
        data-anim="fade"
        data-step="3"
      >
        Team sync
      </text>

      <g stroke={INK} strokeWidth="2.2" fill="none" strokeLinecap="round">
        <path d="M190 160 C220 200 250 210 300 200" data-anim="draw" data-step="3" />
        <path d="M292 193l9 7 -10 5" data-anim="draw" data-step="4" />
      </g>
      <g data-anim="pop" data-step="4">
        <rect
          x="300"
          y="178"
          width="130"
          height="80"
          rx="10"
          fill="#fff"
          stroke={INK}
          strokeWidth="2"
        />
        <g stroke={MUTED} strokeWidth="1.5">
          <line x1="300" y1="204" x2="430" y2="204" />
          <line x1="300" y1="230" x2="430" y2="230" />
          <line x1="365" y1="178" x2="365" y2="258" />
        </g>
      </g>

      <path
        d="M36 300 C70 280 90 305 120 290 S170 270 200 292"
        stroke={PINK}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        data-anim="draw"
        data-step="5"
      />
      <g data-anim="fade" data-step="5">
        <g transform="translate(206 270) rotate(35)">
          <rect x="0" y="0" width="12" height="46" rx="3" fill={PINK} />
          <path d="M0 46 L6 58 L12 46z" fill={INK} />
        </g>
      </g>
    </svg>
  );
}

export function ShareWorkspaceIllustration({ label }: IllustrationProps) {
  const rows = [
    { y: 70, width: 110, active: false },
    { y: 110, width: 90, active: true },
    { y: 150, width: 120, active: false },
    { y: 190, width: 80, active: false }
  ];
  return (
    <svg
      viewBox="0 0 480 320"
      role="img"
      aria-label={label}
      width="100%"
      height="100%"
      data-illustration="share"
    >
      <g data-anim="fade" data-step="0">
        <rect
          x="24"
          y="28"
          width="200"
          height="264"
          rx="14"
          fill="#fff"
          stroke={PINK}
          strokeOpacity="0.35"
          strokeWidth="1.5"
        />
        <rect x="44" y="44" width="70" height="8" rx="4" fill={INK} opacity="0.75" />
      </g>
      {rows.map((row) => (
        <g key={row.y} data-anim="fade" data-step="1">
          <rect
            x="36"
            y={row.y - 14}
            width="176"
            height="30"
            rx="8"
            fill={row.active ? PINK_SOFT : 'transparent'}
          />
          <path
            d={`M48 ${row.y - 6} h10 l4 4 h14 v14 h-28z`}
            fill={row.active ? PINK : GRAY_SOFT}
            stroke={row.active ? PINK : GRAY}
            strokeWidth="1.2"
          />
          <rect
            x="90"
            y={row.y - 3}
            width={row.width}
            height="7"
            rx="3.5"
            fill={row.active ? PINK : MUTED}
            opacity={row.active ? 0.9 : 0.55}
          />
        </g>
      ))}

      <g data-anim="pop" data-step="2">
        <rect
          x="252"
          y="64"
          width="204"
          height="150"
          rx="14"
          fill="#fff"
          stroke={INK}
          strokeOpacity="0.15"
          strokeWidth="1.5"
        />
        <rect x="272" y="84" width="96" height="9" rx="4.5" fill={INK} opacity="0.75" />
        <rect x="272" y="112" width="164" height="30" rx="8" fill={PINK_SOFT} />
        <path
          d="M284 127 a6 6 0 0 1 6-6 h8 M306 127 a6 6 0 0 1-6 6 h-8"
          stroke={PINK}
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
        <rect x="316" y="124" width="100" height="6" rx="3" fill={PINK} opacity="0.6" />
        <g data-idle="pulse">
          <rect x="272" y="160" width="74" height="30" rx="8" fill={PINK} />
          <rect x="286" y="172" width="46" height="6" rx="3" fill="#fff" />
        </g>
        <rect
          x="354"
          y="160"
          width="82"
          height="30"
          rx="8"
          fill="#fff"
          stroke={INK}
          strokeOpacity="0.2"
        />
        <rect x="368" y="172" width="54" height="6" rx="3" fill={INK} opacity="0.4" />
      </g>

      <g transform="translate(300 236)">
        <circle
          cx="16"
          cy="16"
          r="16"
          fill={GRAY_SOFT}
          stroke={GRAY}
          strokeWidth="1.5"
          data-anim="pop"
          data-step="3"
        />
        <circle
          cx="44"
          cy="16"
          r="16"
          fill={PINK_SOFT}
          stroke={PINK}
          strokeWidth="1.5"
          data-anim="pop"
          data-step="3"
        />
        <circle
          cx="72"
          cy="16"
          r="16"
          fill={YELLOW_SOFT}
          stroke="#d9b54a"
          strokeWidth="1.5"
          data-anim="pop"
          data-step="3"
        />
        <text
          x="104"
          y="21"
          fontFamily={HAND_FONT}
          fontSize="14"
          fill={INK}
          data-anim="fade"
          data-step="4"
        >
          +3
        </text>
      </g>
    </svg>
  );
}
